/**
 * DoserneResetPassword — public page that accepts a magic-link token
 * from the query string and lets the user set a new password.
 *
 * Flow:
 *  1. User clicks reset link in email → /doserne/reset-password?token=xxx
 *  2. Chooses new password + confirms → POST /doserne/auth/password-reset/confirm
 *  3. On success, redirects to /doserne/logg-inn with a success toast.
 *
 * Same password policy as the Marketplace admin creates (min 8 chars,
 * upper + lower + digit) — enforced client-side for fast feedback and
 * again server-side for security.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Force no-referrer on this page so the reset token is never leaked
// via the Referer header when the user clicks an outgoing link.
function useNoReferrer() {
  useEffect(() => {
    const prev = document
      .querySelector('meta[name="referrer"]')
      ?.getAttribute("content");
    let meta = document.querySelector('meta[name="referrer"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "referrer");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "no-referrer");
    return () => {
      if (prev) meta.setAttribute("content", prev);
      else meta.remove();
    };
  }, []);
}

function validatePassword(pw) {
  if (pw.length < 8) return "Minst 8 tegn";
  if (!/[a-z]/.test(pw)) return "Må ha minst én liten bokstav";
  if (!/[A-Z]/.test(pw)) return "Må ha minst én stor bokstav";
  if (!/\d/.test(pw)) return "Må ha minst ett siffer";
  return null;
}

export default function DoserneResetPassword() {
  useNoReferrer();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const pwError = useMemo(() => (password ? validatePassword(password) : null), [password]);
  const mismatch = confirm && password && confirm !== password;
  const canSubmit = !!token && !pwError && !mismatch && password.length > 0 && !busy;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!token) return setError("Reset-lenken mangler.");
    if (pwError) return setError(pwError);
    if (mismatch) return setError("Passordene er ikke like.");
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Passordet er endret. Logg inn med det nye passordet.");
      setTimeout(() => navigate("/doserne/logg-inn", { replace: true }), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke tilbakestille passordet.");
      setBusy(false);
    }
  }

  if (done) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="reset-success">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 mb-2">Passordet er endret</h1>
          <p className="text-sm text-slate-500 mb-6">Sender deg til innlogging…</p>
          <Link to="/doserne/logg-inn" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 hover:text-cyan-900">
            Gå til login <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="bg-slate-900 rounded-lg p-2">
              <Lock className="w-5 h-5 text-cyan-300" />
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Doserne</div>
              <div className="text-lg font-black text-white leading-none">Academy</div>
            </div>
          </div>
          <h1 className="text-2xl font-black text-white">Velg nytt passord</h1>
          <p className="text-sm text-slate-400 mt-1">Skriv inn et nytt passord for kontoen din.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6">
          {!token && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="reset-missing-token">
              Ugyldig eller manglende reset-lenke. Be om en ny fra login-siden.
            </div>
          )}

          <form onSubmit={submit} autoComplete="new-password" className="space-y-4">
            <label className="block">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Nytt passord</div>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Minst 8 tegn, stor + liten + siffer"
                  required
                  minLength={8}
                  autoFocus
                  data-testid="reset-password-input"
                />
                <button type="button"
                        onClick={() => setShow((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                        aria-label={show ? "Skjul passord" : "Vis passord"}
                        data-testid="reset-password-toggle">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {pwError && <div className="mt-1 text-xs text-amber-600">{pwError}</div>}
            </label>

            <label className="block">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Gjenta passord</div>
              <input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
                minLength={8}
                data-testid="reset-confirm-input"
              />
              {mismatch && <div className="mt-1 text-xs text-red-600">Passordene er ikke like.</div>}
            </label>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="reset-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 rounded-lg font-semibold text-sm shadow-sm flex items-center justify-center gap-2 transition"
              style={{ color: "#fff" }}
              data-testid="reset-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Lagre nytt passord <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center mt-6">
          <Link to="/doserne/logg-inn" className="hover:text-white">← Tilbake til login</Link>
        </p>
      </div>
    </main>
  );
}
